import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { FetchContactsParams, fetchContacts } from 'api/contacts/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInfiniteScroll } from 'customHooks/useInfiniteScroll';
import { transformInfiniteQueryOptions } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact } from 'types/domain';
import { Meta, PaginatedLink } from 'types/pagination';
import {
    GivebutterInfiniteQueryParams,
    GivebutterUseSuspenseInfiniteQueryOptions,
} from 'types/react-query';

const DEFAULT_PAGE_LIMIT = 20;

const useInfiniteContacts = (
    params: GivebutterInfiniteQueryParams<FetchContactsParams> = {},
    options: GivebutterUseSuspenseInfiniteQueryOptions<Contact> & { enabled?: boolean } = {},
) => {
    const { enabled = true, ...restOptions } = options;
    const { limit = DEFAULT_PAGE_LIMIT, ...rest } = params;
    const nextParams = { limit, ...rest };
    const { accountId } = useCurrentAccount();
    const infiniteQueryData = useSuspenseInfiniteQuery({
        initialPageParam: 1,
        queryKey: accountQueryKeys.contactsWithFilters(accountId, nextParams),
        queryFn: ({ pageParam }) => {
            if (enabled) {
                return fetchContacts(accountId, { page: pageParam, ...nextParams });
            }

            return {
                data: [],
                meta: {} as Meta,
                links: {} as PaginatedLink,
            };
        },
        ...transformInfiniteQueryOptions<Contact>(),
        ...restOptions,
    });
    const ref = useInfiniteScroll(infiniteQueryData);

    return { ...infiniteQueryData, ref };
};

export { useInfiniteContacts };
